/**
 * Pure ciphertext-chunk splitter for the receiver's part-download loop
 * (docs/file-transfer-resume.md §6.2 step 7; TASK-17 brief item 2).
 *
 * A presigned GET for one part is consumed as a `ReadableStream<Uint8Array>`
 * whose individual `read()` calls do NOT align to ciphertext chunk
 * boundaries — a read may deliver as little as one byte, or several chunks'
 * worth at once. `splitCiphertextStream` buffers across reads and yields
 * EXACTLY the expected byte length of each chunk, in order.
 *
 * Mirrors `sidepanel/filetransfer/sendFile.ts`'s `readFixedSizeChunks`,
 * generalized to a per-chunk expected-length LIST rather than one fixed
 * `chunkSize`: every chunk is exactly `CHUNK_CIPHER_SIZE` (65552) bytes
 * EXCEPT the file's very last chunk, which may be shorter (crypto-spec.md
 * §5's `finalLen` rule) — including the 0-byte-file edge case (a single
 * final chunk whose plaintext length is 0, so its ciphertext is exactly
 * `AEAD_OVERHEAD` bytes).
 */

import { AEAD_OVERHEAD } from "@reflecto/core";
import { TransferApiError } from "./errors";

/**
 * Ciphertext byte length of chunk `chunkIndex` (0-based) for a file with
 * `chunkCount` chunks, `fileSize` plaintext bytes, and `chunkSize` plaintext
 * bytes per (non-final) chunk (crypto-spec.md §5 + AEAD_OVERHEAD).
 */
export function chunkCipherLength(
  chunkIndex: number,
  chunkCount: number,
  fileSize: number,
  chunkSize: number
): number {
  const isFinal = chunkIndex === chunkCount - 1;
  const plainLen = isFinal ? fileSize - (chunkCount - 1) * chunkSize : chunkSize;
  return plainLen + AEAD_OVERHEAD;
}

/**
 * Expected ciphertext lengths, in order, for every chunk in
 * `[startChunk, endChunkInclusive]` — the `chunkCipherLengths` list
 * `splitCiphertextStream` needs to slice one part's downloaded bytes.
 */
export function partChunkCipherLengths(
  startChunk: number,
  endChunkInclusive: number,
  chunkCount: number,
  fileSize: number,
  chunkSize: number
): number[] {
  const lens: number[] = [];
  for (let i = startChunk; i <= endChunkInclusive; i++) {
    lens.push(chunkCipherLength(i, chunkCount, fileSize, chunkSize));
  }
  return lens;
}

/**
 * Buffers arbitrary-sized `stream` reads (1-byte reads included) and yields
 * one `Uint8Array` per entry in `chunkCipherLengths`, each exactly that many
 * bytes — regardless of how the underlying reads happened to be chunked,
 * including a single read that straddles (or exceeds) a chunk boundary.
 *
 * Throws if the stream ends before every expected length has been filled
 * (a short/truncated download) — callers surface this as a part-download
 * failure (the receive loop's retry-once policy owns recovery).
 */
export async function* splitCiphertextStream(
  stream: ReadableStream<Uint8Array>,
  chunkCipherLengths: readonly number[]
): AsyncGenerator<Uint8Array> {
  const reader = stream.getReader();
  // Explicit bare `Uint8Array` annotation for the same reason
  // `readFixedSizeChunks` in sendFile.ts uses one: TS narrows `new
  // Uint8Array(0)` to `Uint8Array<ArrayBuffer>`, which the reassigned
  // concatenation result (`Uint8Array<ArrayBufferLike>`) wouldn't satisfy.
  let buffer: Uint8Array = new Uint8Array(0);
  try {
    for (const expectedLen of chunkCipherLengths) {
      while (buffer.length < expectedLen) {
        // BOUNDARY (the #874-class fix): the fetch body is a RAW stream —
        // `fetchTransferPartStream` returns `res.body` unwrapped, and only the
        // INITIAL fetch is wrapped into TransferApiError(0). A connection drop or
        // the part-download deadline firing DURING body consumption rejects this
        // read with a bare TypeError / DOMException("TimeoutError"), which is NOT
        // a TransferApiError — so `isNetworkError` could never match and the
        // receive loop's network-RETRY branch was skipped, terminally destroying
        // a resumable transfer with a wrong INTERNAL wire code. Re-classify a
        // read rejection as the network failure it is, preserving the identity
        // across this boundary. Only the read is wrapped: a clean-but-short
        // stream ("ended early", below) and the consumer's own decrypt/write
        // errors are untouched (a for-await body throw closes this generator via
        // `.return()`, running `finally` only — never this `catch`).
        let result: ReadableStreamReadResult<Uint8Array>;
        try {
          result = await reader.read();
        } catch (readErr) {
          // Duck-type `.message`, NOT `instanceof Error`: the real browser
          // manifestation here is a DOMException ("TimeoutError" / network), which
          // is NOT `instanceof Error` in browser engines — so an instanceof check
          // would drop the useful message for the generic fallback (the #874
          // gotcha, in its harmless spot — classification is unaffected since the
          // wrap is unconditional).
          const readMessage =
            typeof (readErr as { message?: unknown } | null)?.message === "string"
              ? (readErr as { message: string }).message
              : "part stream read failed";
          throw new TransferApiError(0, "network", readMessage);
        }
        const { done, value } = result;
        if (done) {
          throw new Error(
            `ciphertext stream ended early: needed ${expectedLen} bytes, had ${buffer.length}`
          );
        }
        const next = new Uint8Array(buffer.length + value.length);
        next.set(buffer, 0);
        next.set(value, buffer.length);
        buffer = next;
      }
      yield buffer.slice(0, expectedLen);
      buffer = buffer.slice(expectedLen);
    }
  } finally {
    // Unconditional cancel-then-release (review round 2 nit): on a
    // mid-part error (e.g. `ChunkAuthError` triggering
    // `receivePartWithRetry`'s retry-once), the underlying response body
    // must stop streaming into memory immediately rather than draining in
    // the background while the retry opens a second fetch. Calling
    // `cancel()` on the success path too is harmless — a reader whose
    // stream is already fully consumed/closed resolves `cancel()`
    // trivially — so one unconditional call here (rather than branching on
    // exit path) keeps this simpler without any downside.
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
